import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button,
  Modal,
  styled,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { getCharacters } from '../data/characters'; // Import characters data
import { languages, useLanguage, navigateWithLang } from '../data/languages';

const WindowContainer = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ModalContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
}));

const Window = styled(Box)(({ theme }) => ({
  width: '60vw',
  height: '70vh',
  maxWidth: 1600,
  position: 'relative',
  display: 'flex',
  borderRadius: theme.spacing(2),
  backdropFilter: 'blur(20px)',
  '@media (max-width: 1680px), (max-height: 1050px)': {
    width: '70vw',
    height: '75vh',
    maxWidth: 1400,
  },
  '@media (max-width: 1200px)': {
    width: '80vw',
    height: '80vh',
  },
}));

const LeftSection = styled(Box)(({ theme, background }) => ({
  width: '50%',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `url(${background})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(to right, rgba(0,0,0,0.4), rgba(0,0,0,0))',
  },
}));

const RightSection = styled(Box)(({ theme }) => ({
  width: '50%',
  padding: theme.spacing(6),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  position: 'relative',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  overflow: 'auto',
  '@media (max-width: 1680px), (max-height: 1050px)': {
    padding: theme.spacing(4),
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  position: 'relative',
  zIndex: 1,
}));

const NavButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  color: 'rgba(0, 0, 0, 0.7)',
  padding: theme.spacing(2),
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  zIndex: 1400,
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  color: 'rgba(0, 0, 0, 0.7)',
  padding: theme.spacing(1),
  zIndex: 1400,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
}));

export default function CharacterInfoWindow({ 
  open, 
  onClose, 
  character,
  onSelect,
  onCharacterChange,
}) {
  const navigate = useNavigate();
  const theme = useTheme();

  // Check if screen is smaller than 1680x1050
  const isSmallScreen = useMediaQuery('(max-width:1680px), (max-height:1050px)');
  const isVerySmallScreen = useMediaQuery('(max-width:1280px), (max-height:800px)');

  // Text size multiplier for small screens
  const textSizeMultiplier = isVerySmallScreen ? 0.7 : (isSmallScreen ? 0.85 : 1);

  // Language configuration
  const currentLang = useLanguage(); // 响应式语言获取
  const config = languages[currentLang] || languages['en']; // Fallback to English if language not found

  const handleSelect = () => {
    onClose();
    navigateWithLang(navigate, `/detail`, { state: { character, isDailyFortune: false } });
  };

  const handlePrevCharacter = (e) => {
    e.stopPropagation();
    const allCharacters = getCharacters(currentLang);
    const currentIndex = allCharacters.findIndex(char => char.id === character.id);
    const prevIndex = (currentIndex - 1 + allCharacters.length) % allCharacters.length;
    onCharacterChange(allCharacters[prevIndex]);
  };

  const handleNextCharacter = (e) => {
    e.stopPropagation();
    const allCharacters = getCharacters(currentLang);
    const currentIndex = allCharacters.findIndex(char => char.id === character.id);
    const nextIndex = (currentIndex + 1) % allCharacters.length;
    onCharacterChange(allCharacters[nextIndex]);
  };

  return (
    <WindowContainer
      open={open}
      onClose={onClose}
      closeAfterTransition
    >
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <NavButton
          onClick={handlePrevCharacter}
          sx={{ 
            left: theme.spacing(isVerySmallScreen ? 2 : 4),
            padding: isVerySmallScreen ? theme.spacing(1) : theme.spacing(2),
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: isVerySmallScreen ? '1.4rem' : (isSmallScreen ? '1.6rem' : '2rem') }} />
        </NavButton>

        <Window>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
          <LeftSection background={character?.background} />
          <RightSection>
            <ContentWrapper>
              <Box sx={{ 
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                maxWidth: isSmallScreen ? '95%' : '800px',
                maxHeight: isSmallScreen ? '60%' : 'none',
                margin: '0 auto',
                overflow: 'auto',
                padding: isSmallScreen ? theme.spacing(1) : theme.spacing(4),
                ...(isSmallScreen && { 
                  py: 1,
                }),
              }}>
                <Typography sx={{ 
                  fontFamily: "'Recoleta', serif",
                  fontSize: isSmallScreen ? `${1.7 * textSizeMultiplier}rem` : '3.6rem',
                  lineHeight: 1.8,
                  color: 'rgba(0, 0, 0, 0.85)',
                  fontWeight: 400,
                  letterSpacing: '0.03em',
                  textAlign: 'left',
                  width: '100%',
                  wordWrap: 'break-word',
                }}>
                  {character?.description}
                </Typography>
              </Box>

              <Box sx={{
                textAlign: 'right',
                marginTop: isSmallScreen ? 2 : 4,
                marginBottom: isVerySmallScreen ? 1 : 3,
              }}>
                <Typography variant="h1" sx={{
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: 500,
                  fontSize: `${2.0 * textSizeMultiplier}rem`,
                  lineHeight: isSmallScreen ? 1.8 : 2.0,
                  mb: isVerySmallScreen ? 0.5 : (isSmallScreen ? 1 : 2),
                  background: `linear-gradient(45deg, ${character?.colors?.[0] || '#000'}, ${character?.colors?.[1] || '#333'})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  {character?.title}
                </Typography>
                <Typography variant="h3" sx={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontStyle: 'italic',
                  color: character?.colors?.[0] ? `${character.colors[0]}99` : 'rgba(0, 0, 0, 0.6)',
                  fontSize: `${1.5 * textSizeMultiplier}rem`,
                  mb: isVerySmallScreen ? 1 : (isSmallScreen ? 2 : 4),
                }}>
                  {character?.subtitle}
                </Typography>
                <Button
                  variant="contained"
                  size={isVerySmallScreen ? "medium" : "large"}
                  onClick={handleSelect}
                  sx={{
                    minWidth: isVerySmallScreen ? 160 : (isSmallScreen ? 200 : 240),
                    height: isVerySmallScreen ? 40 : (isSmallScreen ? 50 : 60),
                    borderRadius: 4,
                    fontSize: isVerySmallScreen ? '0.9rem' : (isSmallScreen ? '1rem' : '1.2rem'),
                    backgroundColor: character?.colors?.[0] || 'primary.main',
                    boxShadow: 4,
                    '&:hover': {
                      backgroundColor: character?.colors?.[1] || 'primary.dark',
                      boxShadow: 8,
                    },
                  }}
                >
{config.characterInfoWindow.chooseGuide}
                </Button>
              </Box>
            </ContentWrapper>
          </RightSection>
        </Window>

        <NavButton
          onClick={handleNextCharacter}
          sx={{ 
            right: theme.spacing(isVerySmallScreen ? 2 : 4),
            padding: isVerySmallScreen ? theme.spacing(1) : theme.spacing(2),
          }}
        >
          <ChevronRightIcon sx={{ fontSize: isVerySmallScreen ? '1.4rem' : (isSmallScreen ? '1.6rem' : '2rem') }} />
        </NavButton>
      </ModalContent>
    </WindowContainer>
  );
}
